import { createSlice } from "@reduxjs/toolkit";
import { PaginationResponse, ResponseData } from "api/types/_public";
import { IApprovedArticleStatistic, ICategoryStatistic, IDailyStatistic, IHourlyStatistic, IKeywordPublishedStatistic, IKeywordRealTimeStatistic, IMediaStatistic, ISurvivalStatistic } from "api/types/_statistic";
import { approvedArticleStatistics, categoryStatistics, dailyStatistics, hourlyStatistics, keywordPublishedStatistics, keywordRealTimeStatistics, mediaStatistics, survivalStatistics } from "./thunk";



export interface IState {
  daily: ResponseData<IDailyStatistic[]> & PaginationResponse | null,
  isDailyLoading: boolean,
  isDailySuccess: boolean,

  hourly: ResponseData<IHourlyStatistic[]> & PaginationResponse | null,
  isHourlyLoading: boolean,
  isHourlySuccess: boolean,

  survival: ResponseData<ISurvivalStatistic[]> & PaginationResponse | null,
  isSurvivalLoading: boolean,
  isSurvivalSuccess: boolean,

  keywordRealTime: ResponseData<IKeywordRealTimeStatistic[]> & PaginationResponse | null,
  isKeywordRealTimeLoading: boolean,
  isKeywordRealTimeSuccess: boolean,

  keywordPublished: ResponseData<IKeywordPublishedStatistic[]> & PaginationResponse | null,
  isKeywordPublishedLoading: boolean,
  isKeywordPublishedSuccess: boolean,

  media: ResponseData<IMediaStatistic[]> & PaginationResponse | null,
  isMediaLoading: boolean,
  isMediaSuccess: boolean,

  category: ResponseData<ICategoryStatistic[]> & PaginationResponse | null,
  isCategoryLoading: boolean,
  isCategorySuccess: boolean,

  approvedArticle: ResponseData<IApprovedArticleStatistic[]> & PaginationResponse | null,
  isApprovedArticleLoading: boolean,
  isApprovedArticleSuccess: boolean,

  error: any,
};

export const initialState: IState = {
  daily: null,
  isDailyLoading: false,
  isDailySuccess: false,

  hourly: null,
  isHourlyLoading: false,
  isHourlySuccess: false,

  survival: null,
  isSurvivalLoading: false,
  isSurvivalSuccess: false,

  keywordRealTime: null,
  isKeywordRealTimeLoading: false,
  isKeywordRealTimeSuccess: false,

  keywordPublished: null,
  isKeywordPublishedLoading: false,
  isKeywordPublishedSuccess: false,

  media: null,
  isMediaLoading: false,
  isMediaSuccess: false,

  category: null,
  isCategoryLoading: false,
  isCategorySuccess: false,


  approvedArticle: null,
  isApprovedArticleLoading: false,
  isApprovedArticleSuccess: false,

  error: {},
};

const StatisticSlice = createSlice({
  name: "Statistic",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Daily Statistics
    builder.addCase(dailyStatistics.pending, (state: IState, action: any) => {
      state.isDailyLoading = true
    });
    builder.addCase(dailyStatistics.fulfilled, (state: IState, action: any) => {
      state.daily = action.payload.data;
      state.isDailySuccess = true;
      state.isDailyLoading = false;
    });
    builder.addCase(dailyStatistics.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isDailySuccess = false;
      state.isDailyLoading = false;
    });

    // Hourly Statistics
    builder.addCase(hourlyStatistics.pending, (state: IState, action: any) => {
      state.isHourlyLoading = true
    });
    builder.addCase(hourlyStatistics.fulfilled, (state: IState, action: any) => {
      state.hourly = action.payload.data;
      state.isHourlySuccess = true;
      state.isHourlyLoading = false;
    });
    builder.addCase(hourlyStatistics.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isHourlySuccess = false;
      state.isHourlyLoading = false;
    });

    // Survival Statistics
    builder.addCase(survivalStatistics.pending, (state: IState, action: any) => {
      state.isSurvivalLoading = true
    });
    builder.addCase(survivalStatistics.fulfilled, (state: IState, action: any) => {
      state.survival = action.payload.data;
      state.isSurvivalSuccess = true;
      state.isSurvivalLoading = false;
    });
    builder.addCase(survivalStatistics.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isSurvivalSuccess = false;
      state.isSurvivalLoading = false;
    });


    // Keyword Real Time Statistics
    builder.addCase(keywordRealTimeStatistics.pending, (state: IState, action: any) => {
      state.isKeywordRealTimeLoading = true
    });
    builder.addCase(keywordRealTimeStatistics.fulfilled, (state: IState, action: any) => {
      state.keywordRealTime = action.payload.data;
      state.isKeywordRealTimeSuccess = true;
      state.isKeywordRealTimeLoading = false;
    });
    builder.addCase(keywordRealTimeStatistics.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isKeywordRealTimeSuccess = false;
      state.isKeywordRealTimeLoading = false;
    });

    // Keyword Published Statistics
    builder.addCase(keywordPublishedStatistics.pending, (state: IState, action: any) => {
      state.isKeywordPublishedLoading = true
    });
    builder.addCase(keywordPublishedStatistics.fulfilled, (state: IState, action: any) => {
      state.keywordPublished = action.payload.data;
      state.isKeywordPublishedSuccess = true;
      state.isKeywordPublishedLoading = false;
    });
    builder.addCase(keywordPublishedStatistics.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isKeywordPublishedSuccess = false;
      state.isKeywordPublishedLoading = false;
    });

    // Media Statistics
    builder.addCase(mediaStatistics.pending, (state: IState, action: any) => {
      state.isMediaLoading = true
    });
    builder.addCase(mediaStatistics.fulfilled, (state: IState, action: any) => {
      state.media = action.payload.data;
      state.isMediaSuccess = true;
      state.isMediaLoading = false;
    });
    builder.addCase(mediaStatistics.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isMediaSuccess = false;
      state.isMediaLoading = false;
    });

    // Category Statistics
    builder.addCase(categoryStatistics.pending, (state: IState, action: any) => {
      state.isCategoryLoading = true
    });
    builder.addCase(categoryStatistics.fulfilled, (state: IState, action: any) => {
      state.category = action.payload.data;
      state.isCategorySuccess = true;
      state.isCategoryLoading = false;
    });
    builder.addCase(categoryStatistics.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isCategorySuccess = false;
      state.isCategoryLoading = false;
    });

    // Approved Article Statistics
    builder.addCase(approvedArticleStatistics.pending, (state: IState, action: any) => {
      state.isApprovedArticleLoading = true
    });
    builder.addCase(approvedArticleStatistics.fulfilled, (state: IState, action: any) => {
      state.approvedArticle = action.payload.data;
      state.isApprovedArticleSuccess = true;
      state.isApprovedArticleLoading = false;
    });
    builder.addCase(approvedArticleStatistics.rejected, (state: IState, action: any) => {
      state.error = action.payload.error || null;
      state.isApprovedArticleSuccess = false;
      state.isApprovedArticleLoading = false;
    });

  },
});

export default StatisticSlice.reducer;